import React, { useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  INVENTORY_FIELDS, MAX_INVENTORY_ROWS, guessColumns, parseMoney, parseQty, formatPrice,
} from '../../../server/inventory.js';

const { FiX, FiUploadCloud, FiCheckCircle, FiAlertTriangle, FiLoader } = FiIcons;

const SEND_CHUNK = 1000;
const selectClass =
  'w-full min-h-[44px] sm:min-h-0 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 px-3 py-2 text-base sm:text-sm text-slate-900 dark:text-slate-200 focus:border-blue-600 outline-none';

/**
 * Replace the shop's price and stock list from a CSV file:
 * choose file -> confirm which column is which -> upload -> done.
 * The new list only goes live once every row is saved, so a failed upload changes nothing.
 */
const InventoryUploader = ({ currentCount, onClose, onDone }) => {
  const [step, setStep] = useState('choose'); // choose | map | uploading | done
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ sent: 0, total: 0, phase: '' });
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const fields = res.meta.fields || [];
        if (fields.length === 0 || res.data.length === 0) {
          setError('That file has no data rows. Export your list as CSV with a header row and try again.');
          return;
        }
        if (res.data.length > MAX_INVENTORY_ROWS) {
          setError(`That file has ${res.data.length.toLocaleString()} rows. The limit is ${MAX_INVENTORY_ROWS.toLocaleString()}; split it or remove items you don't sell.`);
          return;
        }
        setFileName(file.name);
        setHeaders(fields);
        setRows(res.data);
        setMapping(guessColumns(fields));
        setStep('map');
      },
      error: (err) => setError(`Could not read the file: ${err.message}`),
    });
  };

  const mapped = useMemo(
    () => rows.map((r) => Object.fromEntries(INVENTORY_FIELDS.map((f) => [f.key, mapping[f.key] ? r[mapping[f.key]] : '']))),
    [rows, mapping],
  );

  const stats = useMemo(() => {
    let usable = 0;
    let badPrice = 0;
    let missingPn = 0;
    for (const m of mapped) {
      if (!String(m.pn ?? '').trim()) {
        missingPn += 1;
        continue;
      }
      usable += 1;
      if (String(m.price ?? '').trim() && parseMoney(m.price) === null) badPrice += 1;
    }
    return { usable, badPrice, missingPn };
  }, [mapped]);

  const upload = async () => {
    setError(null);
    setStep('uploading');
    const toSend = mapped.filter((m) => String(m.pn ?? '').trim());
    const batchId = crypto.randomUUID();
    setProgress({ sent: 0, total: toSend.length, phase: 'Uploading' });
    try {
      let accepted = 0;
      let skipped = 0;
      for (let i = 0; i < toSend.length; i += SEND_CHUNK) {
        const res = await api('uploadInventory', { batch_id: batchId, rows: toSend.slice(i, i + SEND_CHUNK) });
        accepted += res.accepted;
        skipped += res.skipped;
        setProgress({ sent: Math.min(i + SEND_CHUNK, toSend.length), total: toSend.length, phase: 'Uploading' });
      }
      setProgress((p) => ({ ...p, phase: 'Switching to the new list' }));
      await api('finishInventory', { batch_id: batchId, count: accepted });
      setResult({ accepted, skipped: skipped + (rows.length - toSend.length) });
      setStep('done');
      // Clear out the old list in the background; it is already hidden, so errors here are harmless.
      (async () => {
        try {
          let remaining = true;
          while (remaining) remaining = (await api('purgeInventory')).remaining;
        } catch { /* leftovers are invisible and cleaned up next time */ }
      })();
    } catch (err) {
      setError(`${err.message} Your current list was not changed.`);
      setStep('map');
      api('purgeInventory', { batch_id: batchId }).catch(() => {});
    }
  };

  const preview = mapped.slice(0, 4);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-950/60 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Upload price and stock list">
      <div className="flex max-h-[100dvh] w-full max-w-2xl flex-col border border-slate-300 bg-white text-left dark:border-slate-800 dark:bg-slate-900 sm:max-h-[90vh]">
        <div className="flex items-start justify-between border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
          <div>
            <div className="caption mb-1">Price and stock list</div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Upload a new list</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <SafeIcon icon={FiX} className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
          {error && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300" role="alert">
              <SafeIcon icon={FiAlertTriangle} className="mt-0.5 h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          {step === 'choose' && (
            <>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Export your price and stock list from your inventory system as a CSV file. The next step lets you confirm which column is which.
                {currentCount > 0 && <> This <strong>replaces</strong> your current list of {currentCount.toLocaleString()} items.</>}
              </p>
              <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <button type="button" onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center gap-2 border-2 border-dashed border-slate-300 py-10 text-slate-600 hover:border-blue-600 hover:text-blue-700 dark:border-slate-700 dark:text-slate-400">
                <SafeIcon icon={FiUploadCloud} className="h-8 w-8" />
                <span className="text-sm font-semibold">Choose a CSV file</span>
              </button>
            </>
          )}

          {step === 'map' && (
            <>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold">{fileName}</span>: {rows.length.toLocaleString()} rows. Check that each field points at the right column.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {INVENTORY_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label htmlFor={`map-${f.key}`} className="caption mb-1 block">{f.label}{f.required ? ' (required)' : ''}</label>
                    <select id={`map-${f.key}`} value={mapping[f.key] || ''} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} className={selectClass}>
                      <option value="">{f.required ? 'Choose a column' : 'Not in my file'}</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {mapping.pn && (
                <div className="overflow-x-auto border border-slate-300 dark:border-slate-700">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-navy text-slate-300">
                      <tr>{['Part number', 'Price', 'Qty', 'SKU', 'Bin'].map((h) => <th key={h} className="px-3 py-2 font-cond font-semibold uppercase tracking-wider">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {preview.map((m, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono">{String(m.pn || '')}</td>
                          <td className="px-3 py-2 font-mono">{formatPrice(parseMoney(m.price)) || '—'}</td>
                          <td className="px-3 py-2 font-mono">{parseQty(m.qty) ?? '—'}</td>
                          <td className="px-3 py-2 font-mono">{String(m.sku || '') || '—'}</td>
                          <td className="px-3 py-2 font-mono">{String(m.bin || '') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <ul className="space-y-1 text-sm">
                <li className="text-slate-700 dark:text-slate-300"><span className="font-mono font-semibold">{stats.usable.toLocaleString()}</span> items will be loaded.</li>
                {stats.missingPn > 0 && <li className="text-amber-800 dark:text-amber-300">{stats.missingPn.toLocaleString()} rows have no part number and will be skipped.</li>}
                {stats.badPrice > 0 && <li className="text-amber-800 dark:text-amber-300">{stats.badPrice.toLocaleString()} prices could not be read and will show no price.</li>}
              </ul>
            </>
          )}

          {step === 'uploading' && (
            <div className="space-y-3 py-6 text-center" aria-live="polite">
              <SafeIcon icon={FiLoader} className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{progress.phase}&hellip;</p>
              <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-800">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress.total ? (progress.sent / progress.total) * 100 : 0}%` }} />
              </div>
              <p className="font-mono text-xs text-slate-500">{progress.sent.toLocaleString()} / {progress.total.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Keep this page open. Your current list stays live until this finishes.</p>
            </div>
          )}

          {step === 'done' && result && (
            <div className="space-y-3 py-6 text-center">
              <SafeIcon icon={FiCheckCircle} className="mx-auto h-10 w-10 text-emerald-600" />
              <p className="text-base font-semibold text-slate-900 dark:text-white">{result.accepted.toLocaleString()} items are live</p>
              {result.skipped > 0 && <p className="text-sm text-slate-600 dark:text-slate-400">{result.skipped.toLocaleString()} rows were skipped (no part number).</p>}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:flex-row sm:justify-end sm:p-5">
          {step === 'done' ? (
            <button type="button" className="btn-primary" onClick={() => { onDone?.(); onClose(); }}>Done</button>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={step === 'uploading'}>Cancel</button>
              {step === 'map' && (
                <button type="button" className="btn-primary" onClick={upload} disabled={!mapping.pn || stats.usable === 0}>
                  Replace list with {stats.usable.toLocaleString()} items
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryUploader;
